import { useState, useEffect } from "react";

export type OpenAiGlobals<
    ToolInput = UnknownObject,
    ToolOutput = UnknownObject,
    ToolResponseMetadata = UnknownObject,
    WidgetState = UnknownObject
> = {
    theme: Theme;

    userAgent: UserAgent;
    locale: string;

    maxHeight: number;
    displayMode: DisplayMode;
    safeArea: SafeArea;

    toolInput: ToolInput;
    toolOutput: ToolOutput | null;
    toolResponseMetadata: ToolResponseMetadata | null;
    widgetState: WidgetState | null;
    setWidgetState: (state: WidgetState) => Promise<void>;
};

type API = {
    callTool: CallTool;
    sendFollowUpMessage: (args: { prompt: string }) => Promise<void>;
    openExternal(payload: { href: string }): void;

    requestDisplayMode: RequestDisplayMode;
};

export type UnknownObject = Record<string, unknown>;

export type Theme = "light" | "dark";

export type SafeAreaInsets = {
    top: number;
    bottom: number;
    left: number;
    right: number;
};

export type SafeArea = {
    insets: SafeAreaInsets;
};

export type DeviceType = "mobile" | "tablet" | "desktop" | "unknown";

export type UserAgent = {
    device: { type: DeviceType };
    capabilities: {
        hover: boolean;
        touch: boolean;
    };
};

export type DisplayMode = "pip" | "inline" | "fullscreen";
export type RequestDisplayMode = (args: { mode: DisplayMode }) => Promise<{
    mode: DisplayMode;
}>;

export type CallToolResponse = {
    content?: Array<Record<string, unknown>>;
    structuredContent?: Record<string, unknown>;
    isError?: boolean;
    [key: string]: unknown;
};

export type CallTool = (
    name: string,
    args: Record<string, unknown>
) => Promise<CallToolResponse>;

export const SET_GLOBALS_EVENT_TYPE = "openai:set_globals";
export class SetGlobalsEvent extends CustomEvent<{
    globals: Partial<OpenAiGlobals>;
}> {
    readonly type = SET_GLOBALS_EVENT_TYPE;
}

declare global {
    interface Window {
        openai: API & OpenAiGlobals;
    }

    interface WindowEventMap {
        [SET_GLOBALS_EVENT_TYPE]: SetGlobalsEvent;
    }
}

export function useOpenAiGlobal<K extends keyof OpenAiGlobals>(
    key: K
): OpenAiGlobals[K] | null {
    const [value, setValue] = useState<OpenAiGlobals[K] | null>(null);

    useEffect(() => {
        if (window.openai[key]) {
            setValue(window.openai[key]);
        }

        const handleSetGlobal = (event: SetGlobalsEvent) => {
            const value = event.detail.globals[key];
            if (value === undefined) {
                return;
            }

            setValue(value);
        };

        window.addEventListener(SET_GLOBALS_EVENT_TYPE, handleSetGlobal, {
            passive: true,
        });

        return () => {
            window.removeEventListener(SET_GLOBALS_EVENT_TYPE, handleSetGlobal);
        };
    }, []);

    return value;
}

export function isFullscreen() {
    return window.openai.displayMode === "fullscreen";
}

export function enterFullscreen() {
    window.openai.requestDisplayMode({ mode: "fullscreen" });
}

export function exitFullscreen() {
    window.openai.requestDisplayMode({ mode: "inline" });
}

export function openExternal(href: string) {
    window.openai.openExternal({ href });
}
